import { motion } from 'framer-motion';
import { PlusCircle, Trash2 } from 'lucide-react';
import { useContext, useMemo, useState } from 'react';
import { useTranslate } from 'sbx-crm-menu-ui';
import { Button } from 'src/components/ui/button';
import { AgentBehavior } from 'src/types/AiAgentV2';
import { SelectComponent } from 'src/utils/UtilsComponent';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { AgentSetupContext } from '../AgentSetupComponent';
import { AppAgentContext } from 'src/context/ContextProvider';
import { grantPermission } from 'src/utils';
import { AI_AGENT_V2_EDIT } from 'src/types/Permission';
import { useParams } from 'react-router-dom';

const classNameLabel = "text-purple-700 font-semibold";
// const classNameInput = "!border-purple-300 !focus:border-purple-500 !focus:ring !focus:ring-purple-200 !focus:ring-opacity-50";

const AgentPolicyComponent = () => {
    const { permissions } = useContext(AppAgentContext).state.session;
    const { t: common } = useTranslate('common');
    const { t: agent } = useTranslate('agent');
    const { setValue, watch, policiesOptions, getValues } = useContext(AgentSetupContext);
    const [optionSelect, setOptionSelect] = useState('');
    const { id } = useParams();

        const allowEdit = useMemo(() => {
        return grantPermission(AI_AGENT_V2_EDIT, permissions);
    }, [permissions]);

    return (
        <Card className="bg-gradient-to-br from-purple-100 to-pink-100 shadow-xl">
            <CardHeader>
                <CardTitle className="text-2xl font-bold text-purple-800">{
                    agent('policy_settings')
                }</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6 flex flex-col">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                >

                <label htmlFor="" className='peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-purple-700 font-semibold text-[16px]'>{agent('select_policy')}</label>
                <div className='flex gap-3 items-center mt-2 mb-5'>
                    <SelectComponent
                        value={optionSelect}
                        onChange={(value) => {
                            setOptionSelect(value)
                        }}
                        options={policiesOptions?.map(item => ({
                                label: item.name,
                                value: item.behavior
                            })).filter(item => !watch('privacy.policies').some(policy => policy.behavior === item.value))}
                        classNameLabel={classNameLabel}
                        required={true}
                        disabled={id !== 'create' && !allowEdit}
                        classNameContent='w-[50%]'
                    />
                    <Button
                        type="button"
                        size={'sm'}
                        className="primary-gradient-button"
                        disabled={!optionSelect || id !== 'create' && !allowEdit}
                        onClick={() => {
                            const selected = policiesOptions?.find(option => option.behavior === optionSelect);
                            if(selected) {
                                setOptionSelect('');
                                const valueSave = [...getValues('privacy.policies'), selected];
                                const value = valueSave.reduce((acc: Omit<AgentBehavior, 'id'>[], val) => {
                                    const option = policiesOptions?.find(item => item.name === val.name);
                                    if(option) {
                                        acc.push({
                                            name: option.name,
                                            behavior: option.behavior,
                                            language: option.language
                                        });
                                    }
                                    return acc;
                                }, []);
                                setValue("privacy.policies", value);
                            }
                        }}
                    >
                        <PlusCircle className=" mr-1 h-4 w-4" />
                        {common('add')}
                    </Button>
                </div>

                <label htmlFor="" className='mt-5 mb-2 peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-purple-700 font-semibold text-[16px]'>{agent('policy_list')}</label>

                <Card className='mt-2 p-2'>
                    <CardContent className='p-2 flex flex-col gap-4'>
                        {
                            watch("privacy.policies").map((option, index) => (
                                <div key={index} className='flex gap-4'>
                                    <Card className='p-2 text-sm flex items-center justify-center w-1/5'>{option.name}</Card>
                                    <Card className='p-2 text-sm w-3/4'>{option.behavior}</Card>
                                    <Card className='p-2 text-sm w-[5%] flex items-center justify-center'>
                                        <Trash2 
                                            className='text-red-500 w-[20px] cursor-pointer transition-all hover:scale-110'
                                            onClick={() => {
                                                const policies = getValues('privacy.policies') || [];
                                                // setOptionsListFilter(prevOptions => ([...prevOptions, option]));
                                                setValue('privacy.policies', policies.filter(option2 => option2.behavior !== option.behavior));
                                            }} 
                                        />
                                    </Card>
                                </div>
                            ))
                        }
                        {
                            watch("privacy.policies").length === 0 && (<div className='text-gray-500 text-sm text-center'>{agent('no_data')}</div>)
                        }
                    </CardContent>
                </Card>
                </motion.div>
            </CardContent>
        </Card>
);
};

export default AgentPolicyComponent;